import { Auth } from 'aws-amplify';
import appConfig from '../config/app-config';

const API = appConfig.apis.signatureApiEnv;
const baseUrl = API.address;

export class ApiError extends Error {
    constructor(public status: number, message: string) {
        super(message);
    }
}

const getHeaders = async (): Promise<Record<string, string>> => {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    return {
        'Content-Type': 'application/json',
        'x-api-key': API.key,
        'Authorization': `Bearer ${token}`,
    };
};

const handleResponse = async (res: Response) => {
    if (res.ok) {
        if (res.status === 204 || res.headers.get('content-length') === '0') return {};
        const text = await res.text();
        if (!text) return {};
        const json = JSON.parse(text);
        // BFF responses (page-list) have data + total — return full object
        if (json.data !== undefined && json.total !== undefined) return json;
        // Envelope responses — unwrap data
        if (json.data !== undefined) return json.data;
        return json;
    }
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body.errors?.[0]?.message || body.message || `API error: ${res.status}`);
};

const get = async (path: string) =>
    handleResponse(await fetch(`${baseUrl}${path}`, { headers: await getHeaders() }));

const post = async (path: string, data: Record<string, unknown>) =>
    handleResponse(await fetch(`${baseUrl}${path}`, { method: 'POST', headers: await getHeaders(), body: JSON.stringify(data) }));

const qs = (params: Record<string, unknown>) => {
    const s = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v != null && v !== '') s.set(k, String(v)); });
    const str = s.toString();
    return str ? `?${str}` : '';
};

export const signatureApi = {
    // Process lifecycle (status, actions)
    getProcessList: (params?: { page?: number; size?: number; status?: string; search?: string }) =>
        get(`/signatures/page-list${qs(params || {})}`),

    getProcessDetail: (id: string) => get(`/signatures/${id}`),

    sendProcess: (id: string, deliveryChannels?: { whatsapp?: boolean; sms?: boolean }) =>
        post(`/signatures/${id}/send`, { deliveryChannels }),

    retryProcess: (id: string, retriedBy: string) => post(`/signatures/${id}/retry`, { retriedBy }),

    cancelProcess: (id: string, cancelReason: string, cancelledBy: string) =>
        post(`/signatures/${id}/cancel`, { cancelReason, cancelledBy }),

    completeProcess: (id: string, completeType: string, completedBy?: string, completeReason?: string) =>
        post(`/signatures/${id}/complete`, { completeType, completedBy, completeReason }),

    freezeProcess: (id: string, reason: string, frozenBy: string) =>
        post(`/signatures/${id}/freeze`, { reason, frozenBy }),

    unfreezeProcess: (id: string) => post(`/signatures/${id}/unfreeze`, {}),

    retrieveProcess: (id: string, retrieveReason: string, retrievedBy: string) =>
        post(`/signatures/${id}/retrieve`, { retrieveReason, retrievedBy }),

    resendToSigner: (processId: string, signerEmail: string) =>
        post(`/signatures/${processId}/resend-signer`, { signerEmail }),

    downloadSigned: (processId: string) =>
        get(`/signatures/${processId}/download`),

    // Providers & Document Types (config)
    getProviders: () => get('/providers'),
    getDocumentTypes: () => get('/document-types'),
};
